import {
    DEFAULT_DESTINATION_EXPANSIONS,
    DestinationEvent,
    transformDestinationEvent,
} from '@eventbrite/event-renderer';
import { sdkRequest } from '@eventbrite/http';
import { deepKeysToCamel } from '@eventbrite/transformation-utils';
import { useInfiniteQuery } from '@tanstack/react-query';
import camelCase from 'lodash/camelCase';
import mapKeys from 'lodash/mapKeys';
import React from 'react';
import { ENTITY_TYPE_MAP, EVENT_TYPE } from '../../../../constants';
import { BucketsContent } from '../../../../types/index';
import { CARD_INFO_LOOKUP_MAP } from '../../../../utils/general';
import { BrowseDataContext } from '../../context';

const transformBuckets = ({
    buckets,
    expanded,
}: {
    buckets: BucketsContent;
    expanded: boolean;
}) => {
    //snake to camel each key
    //if events format the events
    //put events/collections/whatever under "content"
    return buckets.map(({ type, ...bucket }) => {
        let content = [];
        const contentKey = ENTITY_TYPE_MAP[type];
        const shallowCamelBucket = mapKeys(bucket, (_val, key) =>
            camelCase(key),
        );

        if (contentKey) {
            if (type === EVENT_TYPE) {
                content = shallowCamelBucket[contentKey]?.map(
                    (event: DestinationEvent) => ({
                        ...transformDestinationEvent(event),
                        statsigLocationString: `CityBrowse - ${bucket.key}`,
                    }),
                );
            } else {
                const camelBucket = deepKeysToCamel(bucket);
                content = CARD_INFO_LOOKUP_MAP[type]({
                    bucketsContent: camelBucket[contentKey],
                });
            }
        }

        return {
            ...shallowCamelBucket,
            content,
            type,
            isContentExpanded: expanded,
        };
    });
};

const fetchBrowseBuckets = ({ queryKey, pageParam }: any) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [_, placeId, isOnline] = queryKey;
    const { excludedBuckets } = pageParam;

    return sdkRequest('/api/v3/destination/city-browse/', {
        method: 'POST',
        body: JSON.stringify({
            place_id: placeId,
            online_events_only: isOnline,
            buckets_to_exclude: excludedBuckets,
            'expand.destination_event': DEFAULT_DESTINATION_EXPANSIONS,
        }),
    }).then(
        (res: {
            buckets: BucketsContent;
            excluded_buckets: BucketsContent;
            more_buckets: BucketsContent;
        }) => {
            return {
                buckets: transformBuckets({
                    buckets: res.buckets,
                    expanded: true,
                }),
                excludedBuckets: res.excluded_buckets,
                moreBuckets: res.more_buckets,
            };
        },
    );
};

export const MainContentDataLayer = (props: {
    buckets: BucketsContent;
    excludedBuckets?: BucketsContent;
    moreBuckets?: BucketsContent;
    children?: React.ReactNode;
    placeId?: string | null;
    isOnline?: boolean;
}) => {
    const { children, placeId, isOnline } = props;
    const { data, fetchNextPage, hasNextPage } = useInfiniteQuery(
        ['browse-buckets', placeId, isOnline],
        fetchBrowseBuckets as any,
        {
            getNextPageParam: (prevPage) => {
                if (prevPage.moreBuckets) {
                    return {
                        excludedBuckets: prevPage.excludedBuckets,
                    };
                }
            },
            initialData: {
                pages: [
                    {
                        buckets: transformBuckets({
                            buckets: props.buckets,
                            expanded: false,
                        }),
                        excludedBuckets: props.excludedBuckets,
                        moreBuckets: props.moreBuckets,
                    },
                ],
                pageParams: [{ excludedBuckets: props.excludedBuckets }],
            },
        },
    );

    return (
        <BrowseDataContext.Provider
            value={{
                fetchNextPage,
                data,
                hasNextPage,
                buckets: data?.pages.reduce(
                    (memo: any, page) => [...memo, ...page.buckets],
                    [],
                ),
            }}
        >
            {children}
        </BrowseDataContext.Provider>
    );
};
